import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { ButtonGroup, Dialog, FolderTree, TextButton } from '../../../components';
import { PathFolder, useGetFolderPath } from '../../../hooks';
import { useGetFoldersQuery } from '../../../services';

interface SimLocationModalProps {
  isOpen: boolean;
  submitText: string;
  title: string;
  closeModal: () => void;
  onSubmit: (newFolderId: string) => void;
  currFolderId?: string;
  isSubmitting?: boolean;
  orgId?: string;
  setFolderPath?: Dispatch<SetStateAction<PathFolder[]>>;
}

const SimLocationModal = ({
  isOpen,
  submitText,
  title,
  closeModal,
  onSubmit,
  currFolderId,
  isSubmitting,
  orgId,
  setFolderPath,
}: SimLocationModalProps) => {
  // Stores the path of folder IDs leading to the current folder (excluding current folder).
  const [currFolderPath, setCurrFolderPath] = useState<string[]>([]);
  // Currently selected folder in the tree.
  const [selectedFolderId, setSelectedFolderId] = useState(currFolderId || null);

  const { data: folderHierarchy, isLoading: isLoadingFolders } = useGetFoldersQuery({
    all: true,
    includeProspects: false,
    orgId,
  });

  const folders = folderHierarchy?.folders || [];
  const getFolderPath = useGetFolderPath();

  const onCancel = useCallback(() => {
    setSelectedFolderId(currFolderId || null);
    closeModal();
  }, [currFolderId, closeModal]);

  useEffect(() => {
    if (!currFolderId || isLoadingFolders) return;

    // Get the path of folders leading to the current folder.
    const path = getFolderPath(folders, currFolderId) || [];

    if (setFolderPath) {
      setFolderPath(path);
    }

    // Get the IDs of all folders in the path.
    const pathIds = path.map(({ id }) => id);
    setCurrFolderPath(pathIds);
  }, [currFolderId, folders, getFolderPath, isLoadingFolders, setFolderPath]);

  return (
    <Dialog isOpen={isOpen} onClose={closeModal} title={title}>
      <div className="flex h-full flex-col justify-between gap-6">
        <div className="overflow-auto">
          <FolderTree
            folders={folderHierarchy?.folders || []}
            isLoading={isLoadingFolders}
            openFolderIds={currFolderPath}
            selectedFolderId={selectedFolderId}
            setSelectedFolderId={setSelectedFolderId}
          />
        </div>
        <ButtonGroup loading={isSubmitting}>
          <TextButton
            disabled={!selectedFolderId}
            text={submitText}
            onClick={() => onSubmit(selectedFolderId as string)}
          />
          <TextButton text="Cancel" onClick={onCancel} />
        </ButtonGroup>
      </div>
    </Dialog>
  );
};

export default SimLocationModal;
